import { useEffect, useState } from "react";
import { Col, FormGroup, Label, Row } from "reactstrap";
import { useFormikContext } from "formik";
import apiRequestHelper from "../../../../utils/apiRequestHelper";
import { toast } from "react-toastify";

interface Tag {
  id: number;
  name: string;
}

const TagSection = ({ blogId }: { blogId?: number }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);
  const [existingBlogTagIds, setExistingBlogTagIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTags();
    if (blogId) {
      fetchBlogTags();
    }
  }, [blogId]);

  const fetchTags = async () => {
    try {
      const response = await apiRequestHelper.get("/tags");
      setAvailableTags(response.data.data.tags);
    } catch (error: any) {
      toast.error(
        error.response?.data?.messages?.[0]?.message || "Failed to load tags"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogTags = async () => {
    try {
      const response = await apiRequestHelper.get(`/blog-tags/blog/${blogId}`);
      const blogTags = response.data.data.blogTags;
      const tagIds = blogTags.map((bt: any) => bt.tagId);
      const blogTagIds = blogTags.map((bt: any) => bt.id);
      setSelectedTags(tagIds);
      setExistingBlogTagIds(blogTagIds);
      setFieldValue("tagIds", tagIds);
    } catch (error: any) {
      console.error("Error fetching blog tags:", error);
    }
  };

  const handleTagToggle = async (tagId: number) => {
    const isCurrentlySelected = selectedTags.includes(tagId);
    
    if (blogId) {
      // If editing, make API call immediately
      try {
        if (isCurrentlySelected) {
          // Remove tag
          const blogTagId = existingBlogTagIds[selectedTags.indexOf(tagId)];
          await apiRequestHelper.del(`/blog-tags/${blogTagId}`);
          toast.success("Tag removed successfully");
        } else {
          // Add tag
          await apiRequestHelper.post("/blog-tags", { blogId, tagId });
          toast.success("Tag added successfully");
        }
        // Refresh blog tags
        fetchBlogTags();
      } catch (error: any) {
        toast.error(
          error.response?.data?.messages?.[0]?.message || "Failed to update tag"
        );
      }
    } else {
      // If creating, just update local state
      const newSelectedTags = isCurrentlySelected
        ? selectedTags.filter((id) => id !== tagId)
        : [...selectedTags, tagId];
      
      setSelectedTags(newSelectedTags);
      setFieldValue("tagIds", newSelectedTags);
    }
  };

  return (
    <Row>
      <Col>
        <FormGroup>
          <Label>
            Tags <span className="text-muted">(Optional)</span>
          </Label>
          {loading ? (
            <p className="text-muted">Loading tags...</p>
          ) : availableTags.length === 0 ? (
            <p className="text-muted">
              No tags available. Create tags first from the Tags menu.
            </p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {availableTags.map((tag) => (
                <div
                  key={tag.id}
                  onClick={() => handleTagToggle(tag.id)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "20px",
                    border: "2px solid",
                    borderColor: selectedTags.includes(tag.id)
                      ? "#00AC46"
                      : "#ccc",
                    backgroundColor: selectedTags.includes(tag.id)
                      ? "#00AC46"
                      : "transparent",
                    color: selectedTags.includes(tag.id) ? "#fff" : "#333",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          )}
          {selectedTags.length > 0 && (
            <div className="mt-2">
              <small className="text-success">
                {selectedTags.length} tag(s) selected
              </small>
            </div>
          )}
        </FormGroup>
      </Col>
    </Row>
  );
};

export default TagSection;

